<template>
  <div class="row">
    <!-- dashboard -->
    <div  v-if="$can('dashboard.view')">
      <h1 style="font-weight: 700">{{ $t("dashboard.overview") }}</h1>

      <div v-if="hasFace && hasFuel">
        <ul class="nav nav-tabs" id="dashboardTabs" role="tablist">
          <li class="nav-item" role="presentation">
            <button
              class="nav-link active"
              id="fuel-tab"
              data-bs-toggle="tab"
              data-bs-target="#fuel"
              type="button"
              role="tab"
              aria-controls="fuel"
              aria-selected="true"
            >
              Fuel
            </button>
          </li>
          <li v-if="$can('faces.view')" class="nav-item" role="presentation">
            <button
              class="nav-link"
              id="face-tab"
              data-bs-toggle="tab"
              data-bs-target="#face"
              type="button"
              role="tab"
              aria-controls="face"
              aria-selected="false"
            >
              Face
            </button>
          </li>
        </ul>
        <div class="tab-content" id="myTabContent">
          <div
            class="tab-pane fade show active"
            id="fuel"
            role="tabpanel"
            aria-labelledby="fuel-tab"
          >
            <standard
              :hasFace="hasFace"
              @site-selected="handleSiteSelected"
              ref="standardDashboard"
            />
          </div>
          <div
            class="tab-pane fade"
            id="face"
            role="tabpanel"
            aria-labelledby="face-tab"
          >
            <face
              @site-selected="handleSiteSelected"
              ref="faceDashboard"
            ></face>
          </div>
        </div>
      </div>
      <div v-else-if="hasFace">
        <face
          @site-selected="handleSiteSelected"
          ref="faceDashboard"
        ></face>
      </div>
      <div v-else-if="hasFuel">
        <standard
          @site-selected="handleSiteSelected"
          ref="standardDashboard"
        ></standard>
      </div>

      
    </div>
    
    <!-- No dashboard -->
    <WelcomePage v-else />
   
    <!-- always shown -->
    <div v-if="$can('faces.view')"
      class="modal fade"
      id="awaitingEvidenceModal"
      tabindex="-1"
      aria-labelledby="awaitingEvidenceModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header" style="margin-inline: auto">
            <h5 class="modal-title" id="awaitingEvidenceModalLabel" >Incidents Awaiting Evidence</h5>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm position-absolute top-0 end-0 mt-2 me-2"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              X
            </button>
          </div>
          <div
            class="modal-body"
            style="margin-inline: auto"
            v-if="incidentData.incidents && incidentData.incidents.length > 0"
          >
            <p>
              {{
                "There are currently " +
                incidentData.total +
                " Incidents Awaiting new Evidence"
              }}
            </p>
            <ul class="list-group list-group-flush">
              <li
                class="list-group-item"
                v-for="(incident, index) in incidentsWithTimeLimit"
                :key="index"
              >
                <div
                  class="d-flex justify-content-between"
                  @click="goToIncidents(incident.id, selectedSiteId)"
                  style="cursor: pointer;"
                >
                  <span>{{ "Incident " + incident.id }}</span>
                  <span
                    :class="{
                      'text-success': incident.daysLeft > 10,
                      'text-danger': incident.daysLeft < 4,
                      'text-warning':
                        (incident.daysLeft >= 4 && incident.daysLeft <= 10) ||
                        incident.daysLeft === 'Undetermined',
                    }"
                  >
                    {{ incident.daysLeft + " days left" }}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>


  </div>
 
</template>

<script>
import Standard from "./Customised/Standard.vue";
import Board from "./Customised/Board.vue";
import Face from "./Customised/Face.vue";
import clientService from "../../services/client.service";
import dashboardServices from "../../services/dashboard.services";
import WelcomePage from "./WelcomePage.vue";

export default {
  name: "Dashboard",
  components: {
    Standard,
    Board,
    Face,
    WelcomePage,
  },
  data() {
    return {
      hasFace: false,
      hasFuel: false,
      awaitingEvidenceModal: null,
      incidentData: {
        incidents: [],
        total: 0
      },
      selectedSiteId: null,
      userExplicitlySelectedSite: false,
      notifiedIncidentIds: new Set(),
      modalShownSites: new Set(),
      faceSites: [],
    };
  },
  mounted() {
    clientService
      .getClientSites(this.$store.state.auth.user.selectedClient, false)
      .then((response) => {
        const allSites = response.data;
        this.hasFace = allSites.some((site) => site.hasFace);
        this.hasFuel = allSites.some((site) => site.hasFuel);
        
        this.faceSites = allSites.filter(site => site.hasFace);

        const hasOnlyOneSiteTotal = allSites.length === 1;
        const hasOnlyFaceDashboard = this.hasFace && !this.hasFuel;
          
        if ((hasOnlyOneSiteTotal && this.hasFace) || hasOnlyFaceDashboard) {
          if (this.faceSites.length === 1) {
            const siteId = this.faceSites[0].id;
            
            this.$nextTick(() => {
              if (this.$can('faces.view')) {
                this.getAwaitingEvidenceIncidents(siteId, false);
              }
            });
          }
        }
      });

    this.awaitingEvidenceModal = new bootstrap.Modal(
      document.getElementById("awaitingEvidenceModal"),
      {
        keyboard: false,
      }
    );
    
    this.modalShownSites = new Set();
    document.addEventListener('shown.bs.tab', this.handleTabChange);
  },
  beforeUnmount() {
    document.removeEventListener('shown.bs.tab', this.handleTabChange);
  },
  methods: {
    getAwaitingEvidenceIncidents(siteId, forceCheck = false) {
      if (!siteId || !this.$can('faces.view')) return;

      const currentTime = new Date().getTime();
      const siteStorageKey = `lastModalShown_site_${siteId}`;
      const lastShownTime = localStorage.getItem(siteStorageKey);
      const twelveHoursInMs = 12 * 60 * 60 * 1000;

      if (lastShownTime) {
        const timeSinceLastShown = currentTime - parseInt(lastShownTime);
        if (timeSinceLastShown < twelveHoursInMs) {
          return;
        }
      }
      
      if (!forceCheck && this.modalShownSites.has(siteId) && !this.userExplicitlySelectedSite) {
        return;
      } 
      
      dashboardServices.getAwaitingEvidenceIncidents(siteId)
        .then((response) => {
          this.incidentData = response.data;
          if (this.incidentData.incidents && this.incidentData.incidents.length > 0) {
            this.modalShownSites.add(siteId);
            
            localStorage.setItem(siteStorageKey, currentTime.toString());
            this.awaitingEvidenceModal.show();
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            console.log("User doesn't have permission to view Face incidents");
            return;
          }
          this.$error("Failed to load awaiting evidence incidents", error);
        });
    },
    goToIncidents(incidentId, siteId) {
      this.awaitingEvidenceModal.hide();
      this.$router.push(`/faces/incidents/${incidentId}?site_id=${siteId}`);
    },
    handleTabChange(event) {
      if (event.target.id === 'face-tab') {
        if (this.faceSites.length === 1 && this.$can('faces.view')) {
          const siteId = this.faceSites[0].id;

          this.getAwaitingEvidenceIncidents(siteId, false);
        }
      }
    },
    handleSiteSelected(siteId, userExplicitlySelected) {
      if (!siteId) return;
      
      this.selectedSiteId = siteId;
      this.userExplicitlySelectedSite = userExplicitlySelected;
      
      const isFaceDashboard = this.hasFace && !this.hasFuel || 
                            (document.getElementById('face') && 
                              document.getElementById('face').classList.contains('active'));
      
      const shouldCheckAwaitingEvidence = 
        (isFaceDashboard) || 
        (!isFaceDashboard && userExplicitlySelected);
      
      if (this.$can('faces.view') && shouldCheckAwaitingEvidence) {
        this.getAwaitingEvidenceIncidents(siteId);
      }
    }
  },
  computed: {
    incidentsWithTimeLimit() {
      if (!this.incidentData.incidents) return [];
      
      const fourteenDaysInMillis = 14 * 24 * 60 * 60 * 1000;
      const currentDate = new Date();

      return this.incidentData.incidents.map((incident) => {
        if (!incident.latest_evidence) {
          return {
            ...incident,
            daysLeft: "Undetermined",
          };
        } else {
          const latestEvidenceDate = new Date(incident.latest_evidence);
          const timeDiff =
            latestEvidenceDate.getTime() +
            fourteenDaysInMillis -
            currentDate.getTime();
          const daysLeft = Math.ceil(timeDiff / (24 * 60 * 60 * 1000));

          return {
            ...incident,
            daysLeft,
          };
        }
      });
    }
  }
};
</script>

<style scoped>
.wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 50vh;
}
.nav-link {
  font-weight: 700;
  font-size: 1.2rem;
}
</style>
